import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { useMemo } from 'react';

import ItemCard from '@/components/GridItemCard/Card/Card';
import CategorySearchCard from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/CategorySearchCard';
import { ItemCardInfo } from '@/components/GridItemCard/types/types';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getChairishSimilarItems } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.selectors';
import { ChairishItem } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.types';
import { Distance } from '@/types/Distance';
import { SearchParams } from '@/types/SaveSearch';
import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { useSpellCorrectionTerms } from '../../hooks/useSpellCorrectionTerms';
import { SearchPageTabs } from '../SearchPageTabs/SearchPageTabs';
import { SpellCorrectionMessage } from '../SpellCorrectionMessage/SpellCorrectionMessage';
import { ChairishCarousel } from './components/ChairishCarousel/ChairishCarousel';
import { useImageUrls } from './hooks/useImagesUrls';
import { useSimilarActiveItems } from './hooks/useSimilarActiveItems';
import { SearchPageZeroResultsBanner } from './SearchPageZeroResultsBanner';

const DESKTOP_CARD_DIMENSION = 246;
const MOBILE_CARD_DIMENSION = 164;
const EMPTY_CHAIRISH_ITEMS: ChairishItem[] = [];

type PaginationChange = {
    page: number;
    pageSize: number;
};

type SearchPageZeroResultsProps = {
    canonicalURL: string;
    /** The term as shown to people (the SEO term on /search/<slug>/ pages); `searchTerm` stays the raw query. */
    displaySearchTerm: string;
    distance: Distance;
    hasNoFilterTermOrRangeResults: boolean;
    isArchivedSearch: boolean;
    isLoadingSearchResults: boolean;
    isSEOSearchPage: boolean;
    onChange: (change: PaginationChange) => void;
    /**
     * The keyword as the person typed it, from the URL's `originalKeyword` once the page swapped `keyword` for
     * search-party's correction.
     */
    originalSearchTerm?: string;
    page: number;
    pageSize: number;
    saveSearchParams: SearchParams;
    searchTerm: string;
    totalFound: number;
    totalPages: number;
    totalSoldItems: number;
};

export const SearchPageZeroResults = ({
    canonicalURL,
    displaySearchTerm,
    distance,
    hasNoFilterTermOrRangeResults,
    isArchivedSearch,
    isLoadingSearchResults,
    isSEOSearchPage,
    onChange,
    originalSearchTerm,
    page,
    pageSize,
    saveSearchParams,
    searchTerm,
    totalFound,
    totalPages,
    totalSoldItems,
}: SearchPageZeroResultsProps) => {
    const formatMessage = useAppFormatMessage();
    const spellCorrection = useSpellCorrectionTerms({ isLoadingSearchResults, originalSearchTerm, searchTerm });
    const shownSearchTerm = spellCorrection ? cleanSearchTerms(spellCorrection.correctedSearchTerm) : displaySearchTerm;
    const imageUrls = useImageUrls();
    const { firstItemId, similarActiveItemIds } = useSimilarActiveItems();
    const isMobileLayout = useIsMobileLayout();
    const chairishItems = useAppSelector((state) =>
        firstItemId ? getChairishSimilarItems(state, firstItemId) : EMPTY_CHAIRISH_ITEMS
    );

    const similarActiveCards = useMemo(() => {
        const currentDimension = isMobileLayout ? MOBILE_CARD_DIMENSION : DESKTOP_CARD_DIMENSION;

        const itemCardInfo: ItemCardInfo = {
            dimensions: { desktop: currentDimension, mobile: currentDimension },
            ItemCardComponent: CategorySearchCard.ItemCardComponent,
        };

        return similarActiveItemIds.slice(0, 12).map((itemId, index) => ({
            card: (
                <ItemCard
                    itemCardOption={itemCardInfo}
                    itemId={itemId}
                    key={`similar-active-${itemId}`}
                    recommendationPosition={index + 1}
                    recommendationType={LotRecommendationType.YouMayAlsoLike}
                />
            ),
            key: `similar-active-${itemId}`,
        }));
    }, [similarActiveItemIds, isMobileLayout]);

    return (
        <>
            <SearchPageZeroResultsBanner
                imageUrls={imageUrls}
                saveSearchParams={saveSearchParams}
                searchTerm={shownSearchTerm}
                totalSoldItems={totalSoldItems}
            />
            {spellCorrection && (
                <div className="mb-lg smMax:-mx-sm smMax:mb-md smMax:border-b smMax:border-border-tertiary smMax:px-sm">
                    <SpellCorrectionMessage
                        correctedSearchTerm={spellCorrection.correctedSearchTerm}
                        originalSearchTerm={spellCorrection.originalSearchTerm}
                    />
                </div>
            )}
            <div className="mb-2xl desktop:min-h-[469px] smMax:mb-xl">
                <Carousel
                    cards={similarActiveCards}
                    title={formatMessage(['searchPage', 'zeroResults', 'youMayAlsoLike'])}
                />
            </div>
            {chairishItems.length > 0 && (
                <div className="mb-2xl smMax:-mx-sm smMax:mb-xl">
                    <ChairishCarousel items={chairishItems} />
                </div>
            )}
            <div>
                <H2 className="mb-2xs">
                    {formatMessage(['searchPage', 'zeroResults', 'pastResultsTitle'], {
                        values: {
                            plural: totalSoldItems > 1 ? 's' : '',
                            searchTerm: shownSearchTerm,
                            totalSoldItems,
                        },
                    })}
                </H2>
                <BodyPrimary className="text-text-secondary">
                    {formatMessage(['searchPage', 'zeroResults', 'pastResultsBody'])}
                </BodyPrimary>
            </div>
            <Pagination
                canonicalUrl={canonicalURL}
                onChange={onChange}
                pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                totalPages={totalPages}
            >
                <SearchPageTabs
                    distance={distance}
                    hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                    hideTabNavigation
                    isArchivedSearch={isArchivedSearch}
                    isSEOSearchPage={isSEOSearchPage}
                    searchTerm={searchTerm}
                    totalSoldItems={totalSoldItems}
                />
            </Pagination>
        </>
    );
};
